import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { NoIndex } from "@/components/NoIndex";

// Typed wrapper for the beta supabase.auth.oauth namespace.
type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: any }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: any }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: any }>;
};

function getSameOriginPath(): string {
  return window.location.pathname + window.location.search;
}

export default function OAuthConsent() {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("Missing authorization_id in the request.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = getSameOriginPath();
        window.location.href = "/en/auth?next=" + encodeURIComponent(next);
        return;
      }
      const oauth = (supabase.auth as unknown as { oauth: OAuthApi }).oauth;
      const { data, error } = await oauth.getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (error) {
        setError(error.message ?? "Could not load this authorization request.");
        return;
      }
      const immediate = data?.redirect_url ?? data?.redirect_to;
      if (immediate && !data?.client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  async function decide(approve: boolean) {
    setBusy(true);
    setError(null);
    try {
      const oauth = (supabase.auth as unknown as { oauth: OAuthApi }).oauth;
      const { data, error } = approve
        ? await oauth.approveAuthorization(authorizationId)
        : await oauth.denyAuthorization(authorizationId);
      if (error) {
        setError(error.message ?? "Authorization request failed.");
        setBusy(false);
        return;
      }
      const target = data?.redirect_url ?? data?.redirect_to;
      if (!target) {
        setError("No redirect returned by the authorization server.");
        setBusy(false);
        return;
      }
      window.location.href = target;
    } catch (e: any) {
      setError(e?.message ?? "Unexpected error.");
      setBusy(false);
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center p-6 bg-background">
      <NoIndex />
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle>Connect an app to your Aureum7 account</CardTitle>
          <CardDescription>
            Review the request below. You can approve or deny this connection.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {error && (
            <div className="text-sm text-destructive border border-destructive/40 rounded-md p-3">
              {error}
            </div>
          )}
          {!details && !error && (
            <div className="text-sm text-muted-foreground">Loading authorization details…</div>
          )}
          {details && (
            <>
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">Requesting client</div>
                <div className="font-semibold">
                  {details.client?.name ?? details.client?.client_name ?? "External application"}
                </div>
                {details.client?.redirect_uri && (
                  <div className="text-xs text-muted-foreground break-all">
                    Redirect: {details.client.redirect_uri}
                  </div>
                )}
              </div>
              <div className="text-sm">
                This lets{" "}
                <span className="font-medium">
                  {details.client?.name ?? "the client"}
                </span>{" "}
                call this app's enabled MCP tools while you are signed in. It does not bypass
                your permissions or backend policies.
              </div>
              {Array.isArray(details.scopes) && details.scopes.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Requested scopes: {details.scopes.join(", ")}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <Button disabled={busy} onClick={() => decide(true)} className="flex-1">
                  Approve
                </Button>
                <Button
                  disabled={busy}
                  variant="outline"
                  onClick={() => decide(false)}
                  className="flex-1"
                >
                  Cancel connection
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </main>
  );
}
